/**
 * Traps keyboard focus inside the OneTrust consent surface (banner +
 * preference center, both children of `#onetrust-consent-sdk`). Once focus
 * lands inside it, Tab from the last visible control wraps to the first and
 * Shift+Tab from the first wraps to the last, so keyboard users cannot tab
 * past the banner until they close it or save their preferences.
 */
export function applyOneTrustFocusTrap() {
  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget || trapTarget.hasAttribute(TRAP_APPLIED_ATTRIBUTE)) return;

  trapTarget.setAttribute(TRAP_APPLIED_ATTRIBUTE, 'true');
  document.addEventListener('keydown', handleTabKeyDown);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function handleTabKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Tab') return;

  const trapTarget = document.querySelector<HTMLElement>(OT_CONSENT_CONTAINER);

  if (!trapTarget) return;

  const activeElement = document.activeElement;
  const isValidActiveElement = activeElement instanceof HTMLElement;
  const isInsideTarget = trapTarget.contains(activeElement);

  if (!isValidActiveElement || !isInsideTarget) return;

  const focusableElements = getVisibleFocusableElements(trapTarget);

  if (focusableElements.length === 0) return;

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements.at(-1);

  if (!firstFocusable || !lastFocusable) return;

  if (event.shiftKey && activeElement === firstFocusable) {
    event.preventDefault();
    lastFocusable.focus();

    return;
  }

  if (!event.shiftKey && activeElement === lastFocusable) {
    event.preventDefault();
    firstFocusable.focus();
  }
}

function getVisibleFocusableElements(root: HTMLElement) {
  const candidates = [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];

  return candidates.filter(checkIfVisible);
}

function checkIfVisible(element: HTMLElement) {
  if (element.offsetParent === null) return false;

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const OT_CONSENT_CONTAINER = '#onetrust-consent-sdk';
const TRAP_APPLIED_ATTRIBUTE = 'data-keyboard-trap-applied';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
